import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { useIntl } from 'react-intl';
import classnames from 'classnames';

import styles from './Sla.module.scss';
import messages from './messages';

import slaConfig from './slaConfig.json';
import { getCookie } from '../../utils/common';
import {
  PURPLE_PREMIUM_PLUS_SMART_BASE,
  PURPLE_PREMIUM_SMART_BASE,
  REJUVENATE_COLLECTION_PRODUCT_KEYS,
  SLA_RANGE_STANDARD_SHIPPING_7_10_DAYS,
} from '../../utils/common/constants';
import useDrupalSettings from '../../hooks/useDrupalSettings';

const minWeight = 150;

const Sla = ({
  slaMessage,
  productKey,
  isDeliveryStep,
  deliveryType,
  selectedShipWeight,
  selectedBedFrameAOVVariants,
  inline,
}) => {
  const {
    product: productFromDrupalSettings,
    bundle: bundleFromDrupalSettings,
  } = useDrupalSettings();

  const { formatMessage } = useIntl();

  const isOverwritePaidInHomeShippingABTest = !!getCookie(
    process.env.OVERWRITE_PAID_IN_HOME_SHIPPING_AB_TEST_COOKIE_NAME,
  )?.value;

  const hasSmartBaseAOV = !!selectedBedFrameAOVVariants.find((variant) =>
    [PURPLE_PREMIUM_SMART_BASE, PURPLE_PREMIUM_PLUS_SMART_BASE].includes(
      variant.productKey,
    ),
  );
  const isProductMattress = !!slaConfig.mattress.find(
    (product) =>
      product.key === productFromDrupalSettings?.key ||
      product.key === bundleFromDrupalSettings?.[0]?.key,
  );

  const { isMattressSla, isBaseSla, isAccessorySla } = useMemo(() => {
    const isMattress = !!slaConfig.mattress.find(
      ({ key }) => key === productKey,
    );
    const isBase = !!slaConfig.base.find(({ key }) => key === productKey);

    return {
      isMattressSla: isMattress,
      isBaseSla: isBase,
      isAccessorySla: !isMattress && !isBase,
    };
  }, [productKey]);

  const isOverWeight = parseFloat(selectedShipWeight).toFixed(2) >= minWeight;

  const isWhiteGlove =
    slaMessage?.wgRange &&
    (isOverWeight || slaMessage?.wgRange === slaMessage?.range);

  const isRejuvenateMattress =
    REJUVENATE_COLLECTION_PRODUCT_KEYS.includes(productKey);

  const renderAccessorySla = () =>
    slaMessage?.range && (
      <div className={classnames(styles['sla-box'])}>
        <div className={styles['shipping-icon']} />
        <span>{formatMessage(messages.pdpStandardLegend)}</span>
        <span className={styles['range-legend']}>{slaMessage?.range}</span>
      </div>
    );

  const renderDeliveryTypeSla = (delivery) => {
    if (delivery.indexOf('IN-HOME') !== -1) {
      // in home setup
      return (
        <>
          <span className={styles['estimate-message']}>
            {formatMessage(messages.estimatedInHomeMessage)}
          </span>
          <span>{slaMessage.wgRange}</span>
        </>
      );
    }
    if (delivery.indexOf('FEDEX_2_DAY') !== -1) {
      // express shipping
      return (
        <>
          <span className={styles['estimate-message']}>
            {formatMessage(messages.estimatedExpressMessage)}
          </span>
          <span>{formatMessage(messages.daysMessage)}</span>
        </>
      );
    }

    // standard shipping
    return (
      <>
        <span className={styles['estimate-message']}>
          {slaMessage?.range === SLA_RANGE_STANDARD_SHIPPING_7_10_DAYS
            ? formatMessage(messages.estimatedStandardMessage7to10Days)
            : formatMessage(messages.estimatedStandardMessage)}
        </span>
        <span>{slaMessage?.range}</span>
      </>
    );
  };

  const renderDeliverySla = () =>
    slaMessage?.range && (
      <>
        {inline ? (
          <>
            {renderDeliveryTypeSla(deliveryType)}
            <span>{formatMessage(messages.pointAtTheEnd)}</span>
          </>
        ) : (
          <div
            data-testid={`delivery-sla-label-${deliveryType}`}
            className={styles.delivery}
          >
            {renderDeliveryTypeSla(deliveryType)}
          </div>
        )}
      </>
    );

  const renderMattressSla = () => {
    let shippingLegend = formatMessage(messages.pdpStandardLegend);
    let slaRange = slaMessage?.range;

    if (isOverwritePaidInHomeShippingABTest) {
      if (
        (isRejuvenateMattress && isWhiteGlove) ||
        (hasSmartBaseAOV && isProductMattress)
      ) {
        shippingLegend = formatMessage(messages.pdpWhitegloveLegend);
        slaRange = slaMessage?.wgRange;
      } else if (isWhiteGlove) {
        shippingLegend = formatMessage(messages.pdpOverwriteWhitegloveLegend);
        slaRange = slaMessage?.range;
      } else {
        shippingLegend = formatMessage(messages.pdpStandardLegend);
        slaRange = slaMessage?.range;
      }
    } else if (isWhiteGlove) {
      if (!isRejuvenateMattress) {
        shippingLegend = formatMessage(messages.pdpOverwriteWhitegloveLegend);
        slaRange = slaMessage?.range;
      } else {
        shippingLegend = formatMessage(messages.pdpWhitegloveLegend);
        slaRange = slaMessage?.wgRange;
      }
    }

    return (
      <div className={classnames(styles['sla-box'])}>
        <div className={styles['shipping-icon']} />
        <span>{shippingLegend}</span>
        <span className={styles['range-legend']}>{slaRange}</span>
      </div>
    );
  };

  const renderBaseSla = () => (
    <div className={classnames(styles['sla-box'])}>
      <div className={styles['shipping-icon']} />
      <span>{formatMessage(messages.pdpStandardLegend)}</span>
      <span className={styles['range-legend']}>
        {slaMessage?.range ?? slaMessage.fallbackRange}
      </span>
    </div>
  );

  const renderFallbackSla = () => (
    <div className={classnames(styles['sla-box'])}>
      <div className={styles['shipping-icon']} />
      <span>{formatMessage(messages.pdpStandardLegend)}</span>
      <span className={styles['range-legend']}>
        {slaMessage?.fallbackRange}
      </span>
    </div>
  );

  if (!slaMessage) {
    return null;
  }

  const isFallbackRange = !!slaMessage?.fallbackRange;

  return (
    <>
      {isDeliveryStep && renderDeliverySla()}
      {!isDeliveryStep && isFallbackRange && renderFallbackSla()}
      {!isDeliveryStep &&
        isMattressSla &&
        !isFallbackRange &&
        renderMattressSla()}
      {!isDeliveryStep && isBaseSla && !isFallbackRange && renderBaseSla()}
      {!isDeliveryStep &&
        isAccessorySla &&
        !isFallbackRange &&
        renderAccessorySla()}
    </>
  );
};

Sla.propTypes = {
  slaMessage: PropTypes.object,
  productKey: PropTypes.string,
  isDeliveryStep: PropTypes.bool,
  deliveryType: PropTypes.string,
  selectedShipWeight: PropTypes.string,
  selectedBedFrameAOVVariants: PropTypes.arrayOf(PropTypes.object),
  inline: PropTypes.bool,
};

Sla.defaultProps = {
  slaMessage: null,
  productKey: '',
  isDeliveryStep: false,
  deliveryType: '',
  selectedShipWeight: '',
  selectedBedFrameAOVVariants: [],
  inline: false,
};

export default Sla;
